import { useState, useRef } from 'react';
import { api } from '@/lib/api';

/* Encapsulates all mutating case actions (uploads, analysis, exports) and
 * their associated loading flags + file input refs. `loadAll` is the refresh
 * callback from useCaseData. */
export function useCaseActions(caseId, loadAll, setAnalysisProgress) {
  const fileInputRef = useRef(null);
  const bulkInputRef = useRef(null);
  const xlsxInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [xlsxUploading, setXlsxUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [batchAnalyzing, setBatchAnalyzing] = useState(false);
  const [detectingContras, setDetectingContras] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [generatingNotes, setGeneratingNotes] = useState(false);
  const [exporting, setExporting] = useState(null);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/cases/${caseId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const bulkUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setBulkUploading(true);
    try {
      const formData = new FormData();
      for (let f of files) formData.append('files', f);
      await api.post(`/cases/${caseId}/documents/bulk`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
      if (bulkInputRef.current) bulkInputRef.current.value = '';
    }
  };

  const uploadXlsx = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setXlsxUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', 'merge');
      const { data } = await api.post(`/cases/${caseId}/upload-chronology`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert(`Imported ${data.entries_imported} timeline entries`);
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'XLSX import failed');
    } finally {
      setXlsxUploading(false);
      if (xlsxInputRef.current) xlsxInputRef.current.value = '';
    }
  };

  const analyzeDoc = async (docId) => {
    setAnalyzing(docId);
    try {
      await api.post(`/documents/${docId}/analyze`);
      setAnalysisProgress?.({ active: true, operation: 'analyze', total: 1, completed: 0, stage: 'Starting…' });
      // Document is now "processing" - polling will auto-refresh
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(null);
    }
  };

  const runContradictions = async () => {
    setDetectingContras(true);
    try {
      await api.post(`/cases/${caseId}/detect-contradictions`);
      // Runs in background - poll for results
      setTimeout(async () => { await loadAll(); setDetectingContras(false); }, 5000);
      setTimeout(async () => { await loadAll(); }, 15000);
      setTimeout(async () => { await loadAll(); }, 30000);
      setTimeout(async () => { await loadAll(); }, 60000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Detection failed');
      setDetectingContras(false);
    }
  };

  const generateSummary = async () => {
    setGeneratingSummary(true);
    try {
      await api.post(`/cases/${caseId}/generate-summary`);
      // Runs in background - poll for results
      setTimeout(async () => { await loadAll(); setGeneratingSummary(false); }, 5000);
      setTimeout(async () => { await loadAll(); }, 15000);
      setTimeout(async () => { await loadAll(); }, 30000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Summary generation failed');
      setGeneratingSummary(false);
    }
  };

  const batchAnalyze = async (forceReanalyze = false) => {
    if (forceReanalyze) {
      const ok = window.confirm('Re-analyze ALL documents in this case?\n\nThis will delete existing timeline entries, entities and claims for these documents and run the AI extraction again. New timeline entries will replace the old ones.');
      if (!ok) return;
    }
    setBatchAnalyzing(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/analyze-all`, { force_reanalyze: !!forceReanalyze });
      if (data.queued === 0) {
        alert(forceReanalyze ? 'No documents available to reanalyze (need extracted text).' : 'No pending documents to analyze.');
      } else {
        setAnalysisProgress?.({ active: true, operation: 'analyze_all', total: data.queued, completed: 0, stage: 'Starting…' });
      }
      await loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Batch analysis failed');
    } finally {
      setBatchAnalyzing(false);
    }
  };

  const generateNotes = async () => {
    setGeneratingNotes(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/documents/generate-notes`);
      if (data.queued === 0) {
        setGeneratingNotes(false);
        alert('No documents with extracted text to summarize yet.');
        return;
      }
      // Notes are generated in the background — poll a few times to refresh
      setTimeout(async () => { await loadAll(); }, 6000);
      setTimeout(async () => { await loadAll(); }, 15000);
      setTimeout(async () => { await loadAll(); setGeneratingNotes(false); }, 30000);
    } catch (err) {
      setGeneratingNotes(false);
      alert(err.response?.data?.detail || 'Failed to start notes generation');
    }
  };

  const exportFile = async (type, format) => {
    setExporting(`${type}-${format}`);
    try {
      const url = type === 'report' ? `/cases/${caseId}/export/report?format=${format}` : `/cases/${caseId}/export/timeline?format=${format}`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `ClarityCase AI_${type}_${caseId.slice(0, 8)}.${format}`;
      link.click();
    } catch (err) {
      alert('Export failed');
    } finally {
      setExporting(null);
    }
  };

  return {
    fileInputRef, bulkInputRef, xlsxInputRef,
    uploading, bulkUploading, xlsxUploading, analyzing, batchAnalyzing, detectingContras, generatingSummary, exporting,
    uploadFile, bulkUpload, uploadXlsx, analyzeDoc, runContradictions, generateSummary, batchAnalyze, exportFile,
    generatingNotes, generateNotes,
  };
}
